import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./dashboard.css";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import "./forgetStudent.css";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaFilter,
  FaUsers,
  FaUserGraduate,
  FaUserTie,
  FaIdCard,
  FaPhone,
  FaBook,
  FaUsersCog,
  FaCalendarAlt,
  FaEdit,
  FaEye,
  FaMoneyBillWave,
  FaHistory,
  FaChartLine,
  FaCaretDown,
  FaTimes,
  FaSync,
  FaFileExport,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheckCircle,
  FaClock,
  FaSchool,
  FaMapMarkerAlt,
  FaVenusMars,
  FaInfoCircle,
  FaDatabase,
  FaSpinner,
  FaExclamationTriangle,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";

const FetchStudents = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState(false);

  // Auto-suggestions state for all fields
  const [autoSuggestions, setAutoSuggestions] = useState({
    student_name: [],
    father_name: [],
    student_class: [],
    family_status: [],
    mobile: [],
  });

  const [showSuggestions, setShowSuggestions] = useState({
    student_name: false,
    father_name: false,
    student_class: false,
    family_status: false,
    mobile: false,
  });

  const [suggestionLoading, setSuggestionLoading] = useState({
    student_name: false,
    father_name: false,
  });

  const [filters, setFilters] = useState({
    useStudentName: false,
    useFatherName: false,
    useStudentClass: false,
    useFamilyStatus: false,
    useMobile: false,
    student_name: "",
    father_name: "",
    student_class: "",
    family_status: "",
    mobile: "",
  });

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [databaseStats, setDatabaseStats] = useState({
    totalStudents: 0,
    totalClasses: 0,
    totalFamilies: 0,
  });

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Suggestions refs
  const studentNameRef = useRef(null);
  const fatherNameRef = useRef(null);
  const studentClassRef = useRef(null);
  const familyStatusRef = useRef(null);
  const mobileRef = useRef(null);

  // Debounce timer ref
  const debounceTimerRef = useRef(null);

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  const familyStatuses = [
    "Normal",
    "Orphan",
    "Single Parent",
    "Special Needs",
    "Scholarship",
    "Staff Child",
    "Family",
    "Solo",
  ];

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const refs = [
        studentNameRef,
        fatherNameRef,
        studentClassRef,
        familyStatusRef,
        mobileRef,
      ];

      const clickedOutsideAll = refs.every(
        (ref) => ref.current && !ref.current.contains(event.target)
      );

      if (clickedOutsideAll) {
        setShowSuggestions({
          student_name: false,
          father_name: false,
          student_class: false,
          family_status: false,
          mobile: false,
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch database statistics on component mount
  useEffect(() => {
    fetchDatabaseStats();
  }, []);

  // Debounced fetch for suggestions with letter-by-letter autocomplete
  const fetchSuggestions = useCallback(async (field, value) => {
    if (!value || value.trim().length < 2) {
      setAutoSuggestions((prev) => ({ ...prev, [field]: [] }));
      setShowSuggestions((prev) => ({ ...prev, [field]: false }));
      return;
    }

    // Set loading state for specific field
    if (field === "student_name" || field === "father_name") {
      setSuggestionLoading((prev) => ({ ...prev, [field]: true }));
    }

    try {
      let action = "";
      let paramName = "";

      // Determine action and param name based on field
      switch (field) {
        case "student_name":
          action = "autocomplete-student-name";
          paramName = "query";
          break;
        case "father_name":
          action = "autocomplete-father-name";
          paramName = "query";
          break;
        case "student_class":
          action = "autocomplete-class";
          paramName = "class";
          break;
        case "family_status":
          action = "autocomplete-family-status";
          paramName = "status";
          break;
        case "mobile":
          action = "autocomplete-mobile";
          paramName = "mobile";
          break;
        default:
          return;
      }

      const response = await axios.get(
        `https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=${action}&${paramName}=${encodeURIComponent(
          value
        )}`
      );

      if (response.data.success) {
        let suggestions = [];

        // Handle different response formats
        if (Array.isArray(response.data.data)) {
          suggestions = response.data.data;
        } else if (Array.isArray(response.data.suggestions)) {
          suggestions = response.data.suggestions;
        } else if (response.data[field]) {
          suggestions = response.data[field];
        }

        // Transform suggestions to appropriate format
        const formattedSuggestions = suggestions
          .map((item) => {
            if (typeof item === "string") {
              return item;
            } else if (item && typeof item === "object") {
              // Extract the value based on field
              switch (field) {
                case "student_name":
                  return item.name || item.student_name || item.value || "";
                case "father_name":
                  return item.father_name || item.value || "";
                case "student_class":
                  return item.class_name || item.class || item.value || "";
                case "family_status":
                  return item.family_status || item.status || item.value || "";
                case "mobile":
                  return item.mobile || item.phone || item.value || "";
                default:
                  return item.value || "";
              }
            }
            return "";
          })
          .filter(
            (item) => item && item.toLowerCase().includes(value.toLowerCase())
          );

        setAutoSuggestions((prev) => ({
          ...prev,
          [field]: formattedSuggestions.slice(0, 10), // Limit to 10 suggestions
        }));

        setShowSuggestions((prev) => ({
          ...prev,
          [field]: formattedSuggestions.length > 0,
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${field} suggestions:`, error);
      // Fallback to local filtering for certain fields
      performLocalFiltering(field, value);
    } finally {
      if (field === "student_name" || field === "father_name") {
        setSuggestionLoading((prev) => ({ ...prev, [field]: false }));
      }
    }
  }, []);

  // Local filtering fallback
  const performLocalFiltering = (field, value) => {
    let suggestions = [];

    switch (field) {
      case "student_class":
        suggestions = classes.filter((cls) =>
          cls.toLowerCase().includes(value.toLowerCase())
        );
        break;
      case "family_status":
        suggestions = familyStatuses.filter((status) =>
          status.toLowerCase().includes(value.toLowerCase())
        );
        break;
      default:
        suggestions = [];
    }

    setAutoSuggestions((prev) => ({
      ...prev,
      [field]: suggestions.slice(0, 10),
    }));

    setShowSuggestions((prev) => ({
      ...prev,
      [field]: suggestions.length > 0,
    }));
  };

  // Debounced input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer for suggestions
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(name, value);
    }, 300); // 300ms debounce delay
  };

  // Enhanced checkbox change handler
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFilters((prev) => ({ ...prev, [name]: checked }));

    // If unchecked, clear the corresponding field and suggestions
    if (!checked) {
      const fieldMap = {
        useStudentName: "student_name",
        useFatherName: "father_name",
        useStudentClass: "student_class",
        useFamilyStatus: "family_status",
        useMobile: "mobile",
      };

      const fieldToClear = fieldMap[name];
      if (fieldToClear) {
        setFilters((prev) => ({ ...prev, [fieldToClear]: "" }));
        setAutoSuggestions((prev) => ({ ...prev, [fieldToClear]: [] }));
        setShowSuggestions((prev) => ({ ...prev, [fieldToClear]: false }));
      }
    }
  };

  // Handle suggestion click with animation
  const handleSuggestionClick = (suggestion, field) => {
    setFilters((prev) => ({ ...prev, [field]: suggestion }));
    setShowSuggestions((prev) => ({ ...prev, [field]: false }));

    // Highlight the selected field briefly
    const inputElement = document.querySelector(`input[name="${field}"]`);
    if (inputElement) {
      inputElement.classList.add("fetchStudents-highlight");
      setTimeout(() => {
        inputElement.classList.remove("fetchStudents-highlight");
      }, 500);
    }
  };

  // Clear all filters with confirmation
  const handleClearFilters = () => {
    Swal.fire({
      title: "Clear All Filters?",
      text: "This will reset all search filters and clear the results.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Clear All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        setFilters({
          useStudentName: false,
          useFatherName: false,
          useStudentClass: false,
          useFamilyStatus: false,
          useMobile: false,
          student_name: "",
          father_name: "",
          student_class: "",
          family_status: "",
          mobile: "",
        });

        setAutoSuggestions({
          student_name: [],
          father_name: [],
          student_class: [],
          family_status: [],
          mobile: [],
        });

        setShowSuggestions({
          student_name: false,
          father_name: false,
          student_class: false,
          family_status: false,
          mobile: false,
        });

        setStudents([]);
        setFilteredStudents([]);
        setTotalCount(0);
        setCurrentPage(1);

        // Show success message
        Swal.fire({
          title: "Cleared!",
          text: "All filters have been reset.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  // Fetch database statistics
  const fetchDatabaseStats = async () => {
    try {
      const response = await axios.get(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-database-stats"
      );

      if (response.data.success) {
        setDatabaseStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching database stats:", error);
      // Set default stats
      setDatabaseStats({
        totalStudents: 0,
        totalClasses: 0,
        totalFamilies: 0,
      });
    }
  };

  // Enhanced fetch students with better error handling
  const fetchStudents = async () => {
    // Validate at least one filter is enabled
    const activeFilters = Object.keys(filters).filter(
      (key) => key.startsWith("use") && filters[key]
    );

    if (activeFilters.length === 0) {
      Swal.fire({
        title: "No Filters Selected",
        text: "Please enable at least one filter before searching.",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: {
          popup: "swal-wide",
        },
      });
      return;
    }

    setLoading(true);
    try {
      Swal.fire({
        title: "Searching...",
        text: "Please wait while we fetch student data.",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Prepare params
      const params = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key] !== "") {
          params[key] = filters[key];
        }
      });

      const response = await axios.get(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=fetch-students",
        { params }
      );

      Swal.close();

      if (response.data.success) {
        const studentData = response.data.data || [];
        setStudents(studentData);
        setFilteredStudents(studentData);
        setTotalCount(studentData.length);

        // Update database stats if included in response
        if (response.data.stats) {
          setDatabaseStats(response.data.stats);
        }

        // Show success with detailed info
        Swal.fire({
          title: "Search Complete!",
          html: `
            <div style="text-align: center; padding: 20px;">
              <div style="font-size: 48px; color: #28a745; margin-bottom: 10px;">
                <FaUsers />
              </div>
              <h3>Found ${studentData.length} Student${
            studentData.length !== 1 ? "s" : ""
          }</h3>
              <div style="margin-top: 20px; display: flex; justify-content: center; gap: 20px;">
                <div style="text-align: center;">
                  <div style="font-weight: bold; color: #495057;">Filters Used</div>
                  <div style="color: #6c757d; font-size: 14px;">${
                    activeFilters.length
                  }</div>
                </div>
                <div style="text-align: center;">
                  <div style="font-weight: bold; color: #495057;">Active Search</div>
                  <div style="color: #6c757d; font-size: 14px;">${new Date().toLocaleTimeString()}</div>
                </div>
              </div>
            </div>
          `,
          icon: "success",
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        throw new Error(response.data.error || "Failed to fetch students");
      }
    } catch (error) {
      console.error("Error fetching students data:", error);

      Swal.fire({
        title: "Search Failed",
        html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; color: #dc3545; margin-bottom: 10px;">
              <FaExclamationTriangle />
            </div>
            <h3>Unable to fetch students</h3>
            <p style="color: #6c757d; margin-top: 10px;">${error.message}</p>
            <div style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 5px;">
              <p style="margin: 0; font-size: 14px;">Please check your filters and try again.</p>
            </div>
          </div>
        `,
        icon: "error",
        confirmButtonText: "Try Again",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  // Enhanced sorting with animation
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }

    setSortConfig({ key, direction });

    // Add animation class
    const sortedStudents = [...filteredStudents].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      // Convert to string for case-insensitive comparison
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();

      if (direction === "ascending") {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });

    setFilteredStudents(sortedStudents);

    // Scroll to top of table
    const tableContainer = document.querySelector(
      ".fetchStudents-tableContainer"
    );
    if (tableContainer) {
      tableContainer.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Get sort icon with animation
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="fetchStudents-sortIcon" />;
    }
    return sortConfig.direction === "ascending" ? (
      <FaSortUp className="fetchStudents-sortIcon fetchStudents-sortIcon-active" />
    ) : (
      <FaSortDown className="fetchStudents-sortIcon fetchStudents-sortIcon-active" />
    );
  };

  // Handle use ID with enhanced confirmation
  const handleUseID = (studentId) => {
    Swal.fire({
      title: "Proceed with this student?",
      html: `
        <div style="text-align: center;">
          <div style="font-size: 48px; color: #17a2b8; margin-bottom: 15px;">
            <FaIdCard />
          </div>
          <h4>Student ID: <strong>${studentId}</strong></h4>
          <p style="color: #6c757d; margin-top: 10px;">
            You will be redirected to the Fee Management screen.
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Proceed",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#17a2b8",
      cancelButtonColor: "#6c757d",
      customClass: {
        popup: "swal-wide",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/Fee-ApplyTo-Solo-Student?studentId=${studentId}`);
      }
    });
  };

  // Enhanced student details modal
  // Enhanced student details modal
  const viewStudentDetails = (student) => {
    const feeStatus = student.last_pendings > 0 ? "Pending" : "Paid";

    Swal.fire({
      title: "Student Details",
      html: `
      <div class="fetchStudents-studentModal">
        <div class="fetchStudents-studentHeader">
          <div class="fetchStudents-studentAvatar" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
            ${student.name.charAt(0).toUpperCase()}
          </div>
          <div class="fetchStudents-studentBasic">
            <h3>${student.name}</h3>
            <p style="color: #6c757d;">${student.student_id}</p>
            <div class="fetchStudents-statusBadge ${feeStatus.toLowerCase()}" style="margin-top: 5px; display: inline-block;">
              ${feeStatus} Fee: Rs. ${student.last_pendings || 0}
            </div>
          </div>
        </div>
        
        <div class="fetchStudents-studentDetails">
          <div class="fetchStudents-detailRow">
            <div class="fetchStudents-detailItem">
              <span><FaUserTie style="margin-right: 8px; color: #6c757d;" /> Father Name</span>
              <strong>${student.father_name || "N/A"}</strong>
            </div>
            <div class="fetchStudents-detailItem">
              <span><FaSchool style="margin-right: 8px; color: #6c757d;" /> Class & Section</span>
              <strong>${student.class || "N/A"} - ${
        student.section || "N/A"
      }</strong>
            </div>
          </div>
          
          <div class="fetchStudents-detailRow">
            <div class="fetchStudents-detailItem">
              <span><FaPhone style="margin-right: 8px; color: #6c757d;" /> Mobile</span>
              <strong>${student.mobile || "N/A"}</strong>
            </div>
            <div class="fetchStudents-detailItem">
              <span><FaUsersCog style="margin-right: 8px; color: #6c757d;" /> Family Status</span>
              <strong class="fetchStudents-statusBadge ${student.family_status
                ?.toLowerCase()
                .replace(/\s+/g, "-")}">
                ${student.family_status || "Normal"}
              </strong>
            </div>
          </div>
          
          <div class="fetchStudents-detailRow">
            <div class="fetchStudents-detailItem">
              <span><FaIdCard style="margin-right: 8px; color: #6c757d;" /> B-Form No</span>
              <strong>${student.b_form_no || "N/A"}</strong>
            </div>
            <div class="fetchStudents-detailItem">
              <span><FaVenusMars style="margin-right: 8px; color: #6c757d;" /> Gender</span>
              <strong>${student.gender || "N/A"}</strong>
            </div>
          </div>
          
          <div class="fetchStudents-detailItem full-width">
            <span><FaMapMarkerAlt style="margin-right: 8px; color: #6c757d;" /> Address</span>
            <strong>${student.address || "N/A"}</strong>
          </div>
          
          <div class="fetchStudents-detailRow">
            <div class="fetchStudents-detailItem">
              <span><FaCalendarAlt style="margin-right: 8px; color: #6c757d;" /> Admission Date</span>
              <strong>${
                student.created_at
                  ? new Date(student.created_at).toLocaleDateString("en-PK", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "N/A"
              }</strong>
            </div>
            <div class="fetchStudents-detailItem">
              <span><FaMoneyBillWave style="margin-right: 8px; color: #6c757d;" /> Monthly Fee</span>
              <strong>Rs. ${student.monthly_fee || 0}</strong>
            </div>
          </div>
        </div>
        
        <div class="fetchStudents-modalFooter">
          <button 
            id="fetchStudents-closeModalBtn" 
            class="fetchStudents-modalActionBtn" 
            style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);"
          >
            <FaMoneyBillWave style="margin-right: 8px;" /> Close
          </button>
        </div>
      </div>
    `,
      width: "650px",
      showCloseButton: true,
      showConfirmButton: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      backdrop: true,
      customClass: {
        popup: "swal-wide fetchStudents-modal",
        closeButton: "fetchStudents-closeBtn",
      },
      didOpen: () => {
        // Add event listener to the Close button
        const closeButton = document.getElementById(
          "fetchStudents-closeModalBtn"
        );
        if (closeButton) {
          closeButton.addEventListener("click", () => {
            Swal.close();
          });
        }

        // Add animation when modal opens
        const modal = document.querySelector(".swal-wide.fetchStudents-modal");
        if (modal) {
          modal.classList.add("fetchStudents-modal-enter");
        }
      },
      willClose: () => {
        // Animation when modal closes
        const modal = document.querySelector(".swal-wide.fetchStudents-modal");
        if (modal) {
          modal.classList.add("fetchStudents-modal-exit");
        }
      },
    });
  };

  // Enhanced export to CSV
  const exportToCSV = () => {
    if (filteredStudents.length === 0) {
      Swal.fire({
        title: "No Data",
        text: "No students to export.",
        icon: "info",
        confirmButtonText: "OK",
      });
      return;
    }

    Swal.fire({
      title: "Export Data",
      html: `
        <div style="text-align: center;">
          <div style="font-size: 48px; color: #17a2b8; margin-bottom: 15px;">
            <FaFileExport />
          </div>
          <h4>Export ${filteredStudents.length} Students</h4>
          <p style="color: #6c757d; margin-top: 10px;">
            Select export format:
          </p>
          <div style="margin-top: 20px;">
            <button id="exportCSV" class="fetchStudents-exportOptionBtn" style="background: #28a745;">
              Export as CSV
            </button>
            <button id="exportExcel" class="fetchStudents-exportOptionBtn" style="background: #17a2b8; margin-left: 10px;">
              Export as Excel
            </button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      customClass: {
        popup: "swal-wide",
      },
      didOpen: () => {
        document.getElementById("exportCSV").addEventListener("click", () => {
          generateCSV();
          Swal.close();
        });
        document.getElementById("exportExcel").addEventListener("click", () => {
          generateExcel();
          Swal.close();
        });
      },
    });
  };

  const generateCSV = () => {
    const headers = [
      "Student ID",
      "Name",
      "Father Name",
      "Class",
      "Section",
      "Mobile",
      "Family Status",
      "B-Form No",
      "Gender",
      "Address",
      "Monthly Fee",
      "Total Paid",
      "Pending Fee",
    ];

    const csvContent = [
      headers.join(","),
      ...filteredStudents.map((student) =>
        [
          student.student_id,
          `"${student.name}"`,
          `"${student.father_name}"`,
          student.class,
          student.section,
          student.mobile || "N/A",
          student.family_status,
          student.b_form_no || "N/A",
          student.gender || "N/A",
          `"${student.address || "N/A"}"`,
          student.monthly_fee || 0,
          student.total_paid || 0,
          student.last_pendings || 0,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `students_export_${
        new Date().toISOString().split("T")[0]
      }_${Date.now()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      title: "Exported!",
      text: `CSV file with ${filteredStudents.length} records downloaded.`,
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const generateExcel = () => {
    // Simple Excel-like export (CSV with .xls extension)
    const headers = [
      "Student ID",
      "Name",
      "Father Name",
      "Class",
      "Section",
      "Mobile",
      "Family Status",
      "B-Form No",
      "Gender",
      "Address",
      "Monthly Fee",
      "Total Paid",
      "Pending Fee",
    ];

    const excelContent = [
      headers.join("\t"),
      ...filteredStudents.map((student) =>
        [
          student.student_id,
          student.name,
          student.father_name,
          student.class,
          student.section,
          student.mobile || "N/A",
          student.family_status,
          student.b_form_no || "N/A",
          student.gender || "N/A",
          student.address || "N/A",
          student.monthly_fee || 0,
          student.total_paid || 0,
          student.last_pendings || 0,
        ].join("\t")
      ),
    ].join("\n");

    const blob = new Blob([excelContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `students_export_${new Date().toISOString().split("T")[0]}.xls`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Swal.fire({
      title: "Exported!",
      text: `Excel file with ${filteredStudents.length} records downloaded.`,
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  // Enhanced pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStudents = filteredStudents.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of table
    const tableContainer = document.querySelector(
      ".fetchStudents-tableContainer"
    );
    if (tableContainer) {
      tableContainer.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Generate page numbers with ellipsis for large page sets
  const generatePageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(
        1,
        currentPage - Math.floor(maxVisiblePages / 2)
      );
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) pageNumbers.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  // Enhanced search button
  const SearchButton = () => (
    <button
      className="fetchStudents-searchBtn"
      onClick={fetchStudents}
      disabled={loading}
    >
      {loading ? (
        <>
          <div className="fetchStudents-loading"></div>
          <span>Searching...</span>
        </>
      ) : (
        <>
          <FaSearch className="fetchStudents-searchIcon" />
          <span>Search Students</span>
          {Object.values(filters).filter(
            (val) => val && typeof val === "string" && val.trim()
          ).length > 0 && (
            <span className="fetchStudents-activeFilters">
              {
                Object.values(filters).filter(
                  (val) => val && typeof val === "string" && val.trim()
                ).length
              }
            </span>
          )}
        </>
      )}
    </button>
  );

  return (
    <div className="fetchStudents-main">
      <Sidebar />

      <div className="main-content">
        {/* Header */}
        <header className="fetchStudents-header">
          <div className="fetchStudents-headerContent">
            <h1 className="fetchStudents-title">
              <FaUserGraduate className="fetchStudents-titleIcon" />
              Student Search & Management
              <span className="fetchStudents-version">v2.0</span>
            </h1>
            <p className="fetchStudents-subtitle">
              Advanced search, filter, and manage student records with real-time
              suggestions
            </p>
            <div className="fetchStudents-liveStats">
              <div className="fetchStudents-statBadge">
                <FaUsers />
                <span>{databaseStats.totalStudents} Students</span>
              </div>
              <div className="fetchStudents-statBadge">
                <FaSchool />
                <span>{databaseStats.totalClasses} Classes</span>
              </div>
              <div className="fetchStudents-statBadge">
                <FaUsersCog />
                <span>{databaseStats.totalFamilies} Families</span>
              </div>
            </div>
          </div>
        </header>

        {/* Filters Section */}
        <section className="fetchStudents-filtersSection">
          <div className="fetchStudents-filtersCard">
            <div className="fetchStudents-filtersHeader">
              <div className="fetchStudents-filtersTitle">
                <FaFilter className="fetchStudents-filtersIcon" />
                <div>
                  <h2>Advanced Search Filters</h2>
                  <p className="fetchStudents-filtersSubtitle">
                    Enable filters and start typing for instant suggestions
                  </p>
                </div>
              </div>

              <div className="fetchStudents-filtersControls">
                <button
                  className={`fetchStudents-advancedBtn ${
                    advancedFilter ? "active" : ""
                  }`}
                  onClick={() => setAdvancedFilter(!advancedFilter)}
                >
                  <FaCaretDown
                    className={`fetchStudents-caretIcon ${
                      advancedFilter ? "open" : ""
                    }`}
                  />
                  {advancedFilter ? "Hide Advanced" : "Show Advanced"}
                </button>

                <button
                  className="fetchStudents-clearBtn"
                  onClick={handleClearFilters}
                  disabled={
                    students.length === 0 &&
                    Object.values(filters).every((val) => !val)
                  }
                >
                  <FaTimes className="fetchStudents-clearIcon" />
                  Clear All
                </button>
              </div>
            </div>

            <div className="fetchStudents-filtersGrid">
              {/* Student Name Filter with Autocomplete */}
              <div className="fetchStudents-filterGroup">
                <div className="fetchStudents-filterCheckbox">
                  <input
                    type="checkbox"
                    id="useStudentName"
                    name="useStudentName"
                    checked={filters.useStudentName}
                    onChange={handleCheckboxChange}
                    className="fetchStudents-checkbox"
                  />
                  <label
                    htmlFor="useStudentName"
                    className="fetchStudents-checkboxLabel"
                  >
                    <FaUserGraduate className="fetchStudents-checkboxIcon" />
                    Student Name
                    {filters.student_name && (
                      <span className="fetchStudents-filterIndicator"></span>
                    )}
                  </label>
                </div>

                <div
                  className="fetchStudents-inputWrapper"
                  ref={studentNameRef}
                >
                  <div className="fetchStudents-inputGroup">
                    <input
                      type="text"
                      name="student_name"
                      value={filters.student_name}
                      onChange={handleInputChange}
                      placeholder="Start typing student name..."
                      disabled={!filters.useStudentName}
                      className="fetchStudents-input"
                      autoComplete="off"
                    />
                    {suggestionLoading.student_name && (
                      <FaSpinner className="fetchStudents-inputSpinner" />
                    )}
                  </div>

                  {showSuggestions.student_name && (
                    <div className="fetchStudents-suggestions">
                      {autoSuggestions.student_name.map((name, index) => (
                        <div
                          key={index}
                          className="fetchStudents-suggestionItem"
                          onClick={() =>
                            handleSuggestionClick(name, "student_name")
                          }
                        >
                          <FaUserGraduate className="fetchStudents-suggestionIcon" />
                          <span className="fetchStudents-suggestionText">
                            {name}
                          </span>
                          <FaChevronRight className="fetchStudents-suggestionArrow" />
                        </div>
                      ))}
                      {autoSuggestions.student_name.length === 0 && (
                        <div className="fetchStudents-noSuggestions">
                          No suggestions found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Father Name Filter with Autocomplete */}
              <div className="fetchStudents-filterGroup">
                <div className="fetchStudents-filterCheckbox">
                  <input
                    type="checkbox"
                    id="useFatherName"
                    name="useFatherName"
                    checked={filters.useFatherName}
                    onChange={handleCheckboxChange}
                    className="fetchStudents-checkbox"
                  />
                  <label
                    htmlFor="useFatherName"
                    className="fetchStudents-checkboxLabel"
                  >
                    <FaUserTie className="fetchStudents-checkboxIcon" />
                    Father Name
                    {filters.father_name && (
                      <span className="fetchStudents-filterIndicator"></span>
                    )}
                  </label>
                </div>

                <div className="fetchStudents-inputWrapper" ref={fatherNameRef}>
                  <div className="fetchStudents-inputGroup">
                    <input
                      type="text"
                      name="father_name"
                      value={filters.father_name}
                      onChange={handleInputChange}
                      placeholder="Start typing father's name..."
                      disabled={!filters.useFatherName}
                      className="fetchStudents-input"
                      autoComplete="off"
                    />
                    {suggestionLoading.father_name && (
                      <FaSpinner className="fetchStudents-inputSpinner" />
                    )}
                  </div>

                  {showSuggestions.father_name && (
                    <div className="fetchStudents-suggestions">
                      {autoSuggestions.father_name.map((name, index) => (
                        <div
                          key={index}
                          className="fetchStudents-suggestionItem"
                          onClick={() =>
                            handleSuggestionClick(name, "father_name")
                          }
                        >
                          <FaUserTie className="fetchStudents-suggestionIcon" />
                          <span className="fetchStudents-suggestionText">
                            {name}
                          </span>
                          <FaChevronRight className="fetchStudents-suggestionArrow" />
                        </div>
                      ))}
                      {autoSuggestions.father_name.length === 0 && (
                        <div className="fetchStudents-noSuggestions">
                          No suggestions found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Class Filter with Autocomplete */}
              <div className="fetchStudents-filterGroup">
                <div className="fetchStudents-filterCheckbox">
                  <input
                    type="checkbox"
                    id="useStudentClass"
                    name="useStudentClass"
                    checked={filters.useStudentClass}
                    onChange={handleCheckboxChange}
                    className="fetchStudents-checkbox"
                  />
                  <label
                    htmlFor="useStudentClass"
                    className="fetchStudents-checkboxLabel"
                  >
                    <FaSchool className="fetchStudents-checkboxIcon" />
                    Class
                    {filters.student_class && (
                      <span className="fetchStudents-filterIndicator"></span>
                    )}
                  </label>
                </div>

                <div
                  className="fetchStudents-inputWrapper"
                  ref={studentClassRef}
                >
                  <input
                    type="text"
                    name="student_class"
                    value={filters.student_class}
                    onChange={handleInputChange}
                    placeholder="Start typing class..."
                    disabled={!filters.useStudentClass}
                    className="fetchStudents-input"
                    list="classOptions"
                    autoComplete="off"
                  />

                  {showSuggestions.student_class && (
                    <div className="fetchStudents-suggestions">
                      {autoSuggestions.student_class.map((className, index) => (
                        <div
                          key={index}
                          className="fetchStudents-suggestionItem"
                          onClick={() =>
                            handleSuggestionClick(className, "student_class")
                          }
                        >
                          <FaSchool className="fetchStudents-suggestionIcon" />
                          <span className="fetchStudents-suggestionText">
                            {className}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced Filters */}
              {advancedFilter && (
                <>
                  {/* Family Status Filter with Autocomplete */}
                  <div className="fetchStudents-filterGroup">
                    <div className="fetchStudents-filterCheckbox">
                      <input
                        type="checkbox"
                        id="useFamilyStatus"
                        name="useFamilyStatus"
                        checked={filters.useFamilyStatus}
                        onChange={handleCheckboxChange}
                        className="fetchStudents-checkbox"
                      />
                      <label
                        htmlFor="useFamilyStatus"
                        className="fetchStudents-checkboxLabel"
                      >
                        <FaUsersCog className="fetchStudents-checkboxIcon" />
                        Family Status
                        {filters.family_status && (
                          <span className="fetchStudents-filterIndicator"></span>
                        )}
                      </label>
                    </div>

                    <div
                      className="fetchStudents-inputWrapper"
                      ref={familyStatusRef}
                    >
                      <input
                        type="text"
                        name="family_status"
                        value={filters.family_status}
                        onChange={handleInputChange}
                        placeholder="Start typing status..."
                        disabled={!filters.useFamilyStatus}
                        className="fetchStudents-input"
                        autoComplete="off"
                      />

                      {showSuggestions.family_status && (
                        <div className="fetchStudents-suggestions">
                          {autoSuggestions.family_status.map(
                            (status, index) => (
                              <div
                                key={index}
                                className="fetchStudents-suggestionItem"
                                onClick={() =>
                                  handleSuggestionClick(status, "family_status")
                                }
                              >
                                <FaUsersCog className="fetchStudents-suggestionIcon" />
                                <span className="fetchStudents-suggestionText">
                                  {status}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mobile Filter with Autocomplete */}
                  <div className="fetchStudents-filterGroup">
                    <div className="fetchStudents-filterCheckbox">
                      <input
                        type="checkbox"
                        id="useMobile"
                        name="useMobile"
                        checked={filters.useMobile}
                        onChange={handleCheckboxChange}
                        className="fetchStudents-checkbox"
                      />
                      <label
                        htmlFor="useMobile"
                        className="fetchStudents-checkboxLabel"
                      >
                        <FaPhone className="fetchStudents-checkboxIcon" />
                        Mobile Number
                        {filters.mobile && (
                          <span className="fetchStudents-filterIndicator"></span>
                        )}
                      </label>
                    </div>

                    <div className="fetchStudents-inputWrapper" ref={mobileRef}>
                      <input
                        type="text"
                        name="mobile"
                        value={filters.mobile}
                        onChange={handleInputChange}
                        placeholder="Start typing mobile..."
                        disabled={!filters.useMobile}
                        className="fetchStudents-input"
                        autoComplete="off"
                        maxLength="15"
                      />

                      {showSuggestions.mobile && (
                        <div className="fetchStudents-suggestions">
                          {autoSuggestions.mobile.map((number, index) => (
                            <div
                              key={index}
                              className="fetchStudents-suggestionItem"
                              onClick={() =>
                                handleSuggestionClick(number, "mobile")
                              }
                            >
                              <FaPhone className="fetchStudents-suggestionIcon" />
                              <span className="fetchStudents-suggestionText">
                                {number}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Filter Summary */}
            {(filters.useStudentName ||
              filters.useFatherName ||
              filters.useStudentClass ||
              filters.useFamilyStatus ||
              filters.useMobile) && (
              <div className="fetchStudents-filterSummary">
                <div className="fetchStudents-filterSummaryTitle">
                  <FaFilter />
                  Active Filters:
                </div>
                <div className="fetchStudents-filterChips">
                  {filters.useStudentName && filters.student_name && (
                    <span className="fetchStudents-filterChip">
                      Student: {filters.student_name}
                      <FaTimes
                        className="fetchStudents-filterChipClose"
                        onClick={() => {
                          setFilters((prev) => ({ ...prev, student_name: "" }));
                          setAutoSuggestions((prev) => ({
                            ...prev,
                            student_name: [],
                          }));
                        }}
                      />
                    </span>
                  )}
                  {filters.useFatherName && filters.father_name && (
                    <span className="fetchStudents-filterChip">
                      Father: {filters.father_name}
                      <FaTimes
                        className="fetchStudents-filterChipClose"
                        onClick={() => {
                          setFilters((prev) => ({ ...prev, father_name: "" }));
                          setAutoSuggestions((prev) => ({
                            ...prev,
                            father_name: [],
                          }));
                        }}
                      />
                    </span>
                  )}
                  {filters.useStudentClass && filters.student_class && (
                    <span className="fetchStudents-filterChip">
                      Class: {filters.student_class}
                      <FaTimes
                        className="fetchStudents-filterChipClose"
                        onClick={() => {
                          setFilters((prev) => ({
                            ...prev,
                            student_class: "",
                          }));
                          setAutoSuggestions((prev) => ({
                            ...prev,
                            student_class: [],
                          }));
                        }}
                      />
                    </span>
                  )}
                  {filters.useFamilyStatus && filters.family_status && (
                    <span className="fetchStudents-filterChip">
                      Status: {filters.family_status}
                      <FaTimes
                        className="fetchStudents-filterChipClose"
                        onClick={() => {
                          setFilters((prev) => ({
                            ...prev,
                            family_status: "",
                          }));
                          setAutoSuggestions((prev) => ({
                            ...prev,
                            family_status: [],
                          }));
                        }}
                      />
                    </span>
                  )}
                  {filters.useMobile && filters.mobile && (
                    <span className="fetchStudents-filterChip">
                      Mobile: {filters.mobile}
                      <FaTimes
                        className="fetchStudents-filterChipClose"
                        onClick={() => {
                          setFilters((prev) => ({ ...prev, mobile: "" }));
                          setAutoSuggestions((prev) => ({
                            ...prev,
                            mobile: [],
                          }));
                        }}
                      />
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="fetchStudents-actions">
              <SearchButton />

              {students.length > 0 && (
                <button
                  className="fetchStudents-exportBtn"
                  onClick={exportToCSV}
                >
                  <FaFileExport className="fetchStudents-exportIcon" />
                  Export Data
                </button>
              )}

              <button
                className="fetchStudents-refreshBtn"
                onClick={fetchDatabaseStats}
                title="Refresh Database Stats"
              >
                <FaSync className="fetchStudents-refreshIcon" />
              </button>
            </div>
          </div>
        </section>

        {/* Results Section */}
        {students.length > 0 && (
          <section className="fetchStudents-resultsSection">
            <div className="fetchStudents-resultsHeader">
              <div className="fetchStudents-resultsTitle">
                <FaUsers className="fetchStudents-resultsIcon" />
                <div>
                  <h2>Search Results</h2>
                  <p className="fetchStudents-resultsSubtitle">
                    Showing {currentStudents.length} of {totalCount} students
                    {filters.student_class && ` in ${filters.student_class}`}
                  </p>
                </div>
                <span className="fetchStudents-resultsCount">
                  {totalCount} student{totalCount !== 1 ? "s" : ""} found
                </span>
              </div>

              <div className="fetchStudents-resultsStats">
                <div className="fetchStudents-stat">
                  <FaUserGraduate className="fetchStudents-statIcon" />
                  <div>
                    <div className="fetchStudents-statValue">{totalCount}</div>
                    <div className="fetchStudents-statLabel">
                      Total Students
                    </div>
                  </div>
                </div>

                <div className="fetchStudents-stat">
                  <FaSchool className="fetchStudents-statIcon" />
                  <div>
                    <div className="fetchStudents-statValue">
                      {[...new Set(students.map((s) => s.class))].length}
                    </div>
                    <div className="fetchStudents-statLabel">Classes</div>
                  </div>
                </div>

                <div className="fetchStudents-stat">
                  <FaMoneyBillWave className="fetchStudents-statIcon" />
                  <div>
                    <div className="fetchStudents-statValue">
                      Rs.{" "}
                      {students
                        .reduce((sum, s) => sum + (s.last_pendings || 0), 0)
                        .toLocaleString("en-IN")}
                    </div>
                    <div className="fetchStudents-statLabel">Total Pending</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table Container */}
            <div className="fetchStudents-tableContainer">
              <div className="fetchStudents-tableWrapper">
                <table className="fetchStudents-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("student_id")}>
                        <div className="fetchStudents-thContent">
                          <FaIdCard className="fetchStudents-thIcon" />
                          Student ID
                          {getSortIcon("student_id")}
                        </div>
                      </th>
                      <th onClick={() => handleSort("name")}>
                        <div className="fetchStudents-thContent">
                          <FaUserGraduate className="fetchStudents-thIcon" />
                          Student Name
                          {getSortIcon("name")}
                        </div>
                      </th>
                      <th onClick={() => handleSort("father_name")}>
                        <div className="fetchStudents-thContent">
                          <FaUserTie className="fetchStudents-thIcon" />
                          Father Name
                          {getSortIcon("father_name")}
                        </div>
                      </th>
                      <th onClick={() => handleSort("class")}>
                        <div className="fetchStudents-thContent">
                          <FaBook className="fetchStudents-thIcon" />
                          Class
                          {getSortIcon("class")}
                        </div>
                      </th>
                      <th onClick={() => handleSort("section")}>
                        <div className="fetchStudents-thContent">
                          Section
                          {getSortIcon("section")}
                        </div>
                      </th>
                      <th>
                        <div className="fetchStudents-thContent">
                          <FaPhone className="fetchStudents-thIcon" />
                          Mobile
                        </div>
                      </th>
                      <th onClick={() => handleSort("family_status")}>
                        <div className="fetchStudents-thContent">
                          <FaUsersCog className="fetchStudents-thIcon" />
                          Family Status
                          {getSortIcon("family_status")}
                        </div>
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStudents.map((student, index) => (
                      <tr
                        key={student.student_id}
                        className={`fetchStudents-tableRow ${
                          index % 2 === 0 ? "even" : "odd"
                        }`}
                      >
                        <td>
                          <div className="fetchStudents-studentId">
                            <FaIdCard className="fetchStudents-studentIdIcon" />
                            <div className="fetchStudents-studentIdText">
                              {student.student_id}
                              <div className="fetchStudents-studentIdBadge">
                                {student.last_pendings > 0 ? "Pending" : "Paid"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="fetchStudents-studentInfo">
                            <div className="fetchStudents-avatar">
                              {student.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="fetchStudents-nameInfo">
                              <div className="fetchStudents-name">
                                {student.name}
                              </div>
                              <div className="fetchStudents-email">
                                {student.email || "No email"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="fetchStudents-fatherName">
                            <FaUserTie className="fetchStudents-fatherIcon" />
                            {student.father_name}
                          </div>
                        </td>
                        <td>
                          <span className="fetchStudents-classBadge">
                            <FaBook className="fetchStudents-classIcon" />
                            {student.class}
                          </span>
                        </td>
                        <td>
                          <span className="fetchStudents-section">
                            {student.section}
                          </span>
                        </td>
                        <td>
                          <div className="fetchStudents-mobile">
                            <FaPhone className="fetchStudents-mobileIcon" />
                            {student.mobile || "N/A"}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`fetchStudents-statusBadge ${student.family_status
                              ?.toLowerCase()
                              .replace(/\s+/g, "-")}`}
                          >
                            {student.family_status}
                          </span>
                        </td>
                        <td>
                          <div className="fetchStudents-actionsCell">
                            <button
                              className="fetchStudents-viewBtn"
                              onClick={() => viewStudentDetails(student)}
                              title="View Complete Details"
                            >
                              <FaEye />
                            </button>

                            <button
                              className="fetchStudents-feeBtn"
                              onClick={() => handleUseID(student.student_id)}
                              title="Manage Student Fees"
                            >
                              <FaMoneyBillWave />
                            </button>

                            <button
                              className="fetchStudents-useBtn"
                              onClick={() => handleUseID(student.student_id)}
                              title="Use This Student ID"
                            >
                              <FaEdit />
                              Use ID
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <div className="fetchStudents-pagination">
                  <button
                    className="fetchStudents-paginationBtn"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft />
                    Previous
                  </button>

                  <div className="fetchStudents-pageNumbers">
                    {generatePageNumbers().map((number, index) =>
                      number === "..." ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="fetchStudents-pageEllipsis"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={number}
                          className={`fetchStudents-pageBtn ${
                            currentPage === number ? "active" : ""
                          }`}
                          onClick={() => paginate(number)}
                        >
                          {number}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    className="fetchStudents-paginationBtn"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <FaChevronRight />
                  </button>

                  <div className="fetchStudents-paginationInfo">
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              )}

              {/* No Results */}
              {filteredStudents.length === 0 && (
                <div className="fetchStudents-noResults">
                  <FaDatabase className="fetchStudents-noResultsIcon" />
                  <h3>No Students Found</h3>
                  <p>Try adjusting your search filters</p>
                  <button
                    className="fetchStudents-clearBtn"
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Quick Stats & Database Info */}
        {students.length === 0 && (
          <section className="fetchStudents-quickStats">
            <div className="fetchStudents-statsCard">
              <div className="fetchStudents-statsHeader">
                <FaChartLine className="fetchStudents-statsIcon" />
                <h3>Student Database</h3>
                <button
                  className="fetchStudents-refreshBtn"
                  onClick={fetchDatabaseStats}
                  title="Refresh Stats"
                >
                  <FaSync />
                </button>
              </div>

              <div className="fetchStudents-statsGrid">
                <div className="fetchStudents-quickStat">
                  <div className="fetchStudents-quickStatIcon">
                    <FaUserGraduate />
                  </div>
                  <div className="fetchStudents-quickStatContent">
                    <div className="fetchStudents-quickStatValue">
                      {databaseStats.totalStudents}
                    </div>
                    <div className="fetchStudents-quickStatLabel">Students</div>
                  </div>
                </div>

                <div className="fetchStudents-quickStat">
                  <div className="fetchStudents-quickStatIcon">
                    <FaSchool />
                  </div>
                  <div className="fetchStudents-quickStatContent">
                    <div className="fetchStudents-quickStatValue">
                      {databaseStats.totalClasses}
                    </div>
                    <div className="fetchStudents-quickStatLabel">Classes</div>
                  </div>
                </div>

                <div className="fetchStudents-quickStat">
                  <div className="fetchStudents-quickStatIcon">
                    <FaUsers />
                  </div>
                  <div className="fetchStudents-quickStatContent">
                    <div className="fetchStudents-quickStatValue">
                      {databaseStats.totalFamilies}
                    </div>
                    <div className="fetchStudents-quickStatLabel">Families</div>
                  </div>
                </div>
              </div>

              <div className="fetchStudents-statsHint">
                <FaInfoCircle className="fetchStudents-hintIcon" />
                <div>
                  <p>
                    <strong>Tip:</strong> Enable filters above and start typing
                    for instant suggestions
                  </p>
                  <ul className="fetchStudents-hintList">
                    <li>Type at least 2 characters for suggestions</li>
                    <li>Use checkboxes to activate filter fields</li>
                    <li>Click suggestions to autofill</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Database Health Check */}
            <div className="fetchStudents-dbHealth">
              <div className="fetchStudents-dbHealthHeader">
                <FaDatabase className="fetchStudents-dbHealthIcon" />
                <h4>Database Status</h4>
              </div>
              <div className="fetchStudents-dbHealthStatus">
                <div className="fetchStudents-dbHealthItem">
                  <span>Connection:</span>
                  <span className="fetchStudents-dbHealthStatusIndicator active">
                    Active
                  </span>
                </div>
                <div className="fetchStudents-dbHealthItem">
                  <span>Last Updated:</span>
                  <span>{new Date().toLocaleString()}</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="fetchStudents-footer">
          <div className="fetchStudents-footerContent">
            <div className="fetchStudents-footerLeft">
              <div className="fetchStudents-footerLogo">
                <FaUserGraduate className="fetchStudents-footerIcon" />
                Student Management System v2.0
              </div>
              <p className="fetchStudents-footerText">
                Advanced search with real-time suggestions • Professional
                interface
                {students.length > 0 && ` • Showing ${students.length} records`}
              </p>
            </div>

            <div className="fetchStudents-footerRight">
              <div className="fetchStudents-systemStatus">
                <div className="fetchStudents-statusDot active"></div>
                <div>
                  <div className="fetchStudents-statusText">System Active</div>
                  <div className="fetchStudents-statusSubtext">
                    {students.length > 0
                      ? "Search Complete"
                      : "Ready for Search"}
                  </div>
                </div>
              </div>
              <div className="fetchStudents-lastUpdated">
                Last updated:{" "}
                {new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default FetchStudents;
